import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { nanoid } from 'nanoid';
import { env } from '@/lib/env';
import { getAuthItem } from '@/lib/authStorage';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import type { EmailResponse } from '@/types/modules';
import { CloseButton } from '@/common/components/CloseButton';
import { AIResponsePanel } from './AIResponsePanel';
import { SnippetLibrary } from './SnippetLibrary';
import { ImageLightbox } from '@/common/components/ImageLightbox';
import { useImageDropZone } from '@/common/hooks/useImageDropZone';
import { emailApiBase } from '@/lib/email-api'
import {
  buildDraftStashKey as buildDraftStashKeyFor,
  readDraftStash,
  writeDraftStash,
  clearDraftStash,
} from './composeDraftStash';

function buildStashKey(contact: { id?: string; email?: string }): string | null {
  return buildDraftStashKeyFor(contact, getActiveWorkspaceId());
}

interface Contact {
  id?: string;
  name?: string;
  firstName?: string;
  email?: string;
  emails?: string[];
  company?: string;
  organization?: string;
  stage?: string;
  notes?: Array<{ id: string; content: string; createdAt: string }>;
}

interface ComposeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: Contact;
  template?: { subject: string; body: string };
  responses?: EmailResponse[];
  stages?: string[];  // All pipeline stage names, for the response editor
  onSaveResponses?: (responses: EmailResponse[]) => void;
  onSend?: (details: { to: string; subject: string; body: string; attachmentNames: string[] }) => void;
}

function getHeaders(): Record<string, string> {
  const token = getAuthItem('xavior_auth_token');
  const userId = getAuthItem('userId');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(userId ? { 'X-User-Id': userId } : {}),
  };
}

/** Strip HTML tags and decode entities to plain text */
function htmlToText(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

function renderTemplate(template: string, contact: Contact): string {
  const name = contact.name || '';
  const firstName = contact.firstName || name.split(' ')[0] || '';
  const company = contact.company || contact.organization || '';
  const email = contact.email || contact.emails?.[0] || '';

  return template
    .replace(/\{\{name\}\}/gi, name)
    .replace(/\{\{firstName\}\}/gi, firstName)
    .replace(/\{\{company\}\}/gi, company)
    .replace(/\{\{email\}\}/gi, email);
}

function getInitialBody(contact: Contact, template?: { subject: string; body: string }): string {
  if (template?.body) return renderTemplate(template.body, contact);
  const firstName = contact.firstName || (contact.name || '').split(' ')[0] || '';
  return firstName ? `Hey ${firstName},\n\n` : '';
}

function getInitialSubject(contact: Contact, template?: { subject: string; body: string }): string {
  if (template?.subject) return renderTemplate(template.subject, contact);
  return 'Following up';
}

/** Filter responses relevant to this contact's stage */
function getRelevantResponses(responses: EmailResponse[], stage?: string): EmailResponse[] {
  return responses.filter(r => {
    if (!r.stages || r.stages.length === 0) return true; // No stage filter = show everywhere
    return stage ? r.stages.includes(stage) : true;
  });
}

// ── Views ──────────────────────────────────────────────────

type ModalView = 'compose' | 'manage' | 'edit-response' | 'ai-response';

export function ComposeEmailModal({
  isOpen,
  onClose,
  contact,
  template,
  responses = [],
  stages = [],
  onSaveResponses,
  onSend,
}: ComposeEmailModalProps) {
  // Compose state
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Attachment state
  interface PendingAttachment {
    id: string;
    filename: string;
    mimeType: string;
    data: string; // base64
    size: number;
    previewUrl?: string;
  }
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  // Revoke any outstanding image preview URLs when the modal unmounts.
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  useEffect(() => () => {
    attachmentsRef.current.forEach(a => { if (a.previewUrl) URL.revokeObjectURL(a.previewUrl); });
  }, []);

  // View state
  const [view, setView] = useState<ModalView>('compose');

  // Response editor state
  const [editingResponse, setEditingResponse] = useState<EmailResponse | null>(null);
  const [editName, setEditName] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editStages, setEditStages] = useState<string[]>([]);

  // Snippet library
  const [showSnippetLibrary, setShowSnippetLibrary] = useState(false);

  // AI draft generation state
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);
  const userEditedRef = useRef(false);
  const threadsLoadedRef = useRef(false);
  const [aiContext, setAiContext] = useState<{
    stage: string; sentiment: string; interestLevel: string;
    relationshipState: string; followUpScore: number | null;
    summary: string; nextStep: string; recentNotes: string[];
  } | null>(null);

  // Email thread history
  interface EmailThread {
    id: string;
    subject: string;
    from: string;
    date: string;
    snippet: string;
    inbound: boolean; // from the contact to us
  }
  interface ThreadMessage {
    from: string;
    date: string;
    body: string;
    inbound: boolean;
  }
  const [emailThreads, setEmailThreads] = useState<EmailThread[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);
  const [threadMessages, setThreadMessages] = useState<Record<string, ThreadMessage[]>>({});
  const [loadingThreadDetail, setLoadingThreadDetail] = useState<string | null>(null);
  const [latestMessage, setLatestMessage] = useState<{ subject: string; body: string; date: string; from: string; inbound: boolean } | null>(null);
  const [loadingLatestInbound, setLoadingLatestInbound] = useState(false);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      // Recover any stashed draft from a previous dismiss whose upstream
      // POST never succeeded (4xx / network failure). The stash is scoped
      // per-workspace+contact and cleared on confirmed 2xx in handleDismiss.
      // readDraftStash enforces the #2715 TTL guard — stashes older than
      // DRAFT_STASH_TTL_MS are discarded so abandoned drafts containing
      // sensitive content don't linger indefinitely.
      const recovered = readDraftStash(buildStashKey(contact));
      setTo(recovered?.to || contact.email || contact.emails?.[0] || '');
      setSubject(recovered?.subject ?? getInitialSubject(contact, template));
      setBody(recovered?.body ?? getInitialBody(contact, template));
      // If we recovered, the body is already user-edited content — flag it
      // so the AI-draft replacement doesn't overwrite the recovered draft.
      userEditedRef.current = Boolean(recovered);
      setIsSending(false);
      setIsSavingDraft(false);
      setError(null);
      setSent(false);
      setSavedDraft(false);
      setView('compose');
      setEditingResponse(null);
      setAiContext(null);
      setEmailThreads([]);
      setExpandedThreadId(null);
      setThreadMessages({});
      setLatestMessage(null);
      setLoadingLatestInbound(false);
      threadsLoadedRef.current = false;
      setAttachments(prev => {
        prev.forEach(a => { if (a.previewUrl) URL.revokeObjectURL(a.previewUrl); });
        return [];
      });
    }
  }, [isOpen, contact]);

  // Fetch email history then generate AI draft — combined into one effect to avoid
  // React cleanup race conditions (loadingThreads state changes were cancelling the draft fetch)
  useEffect(() => {
    if (!isOpen || !contact.id) return;
    if (threadsLoadedRef.current) return; // only run once per modal open
    threadsLoadedRef.current = true;

    let cancelled = false;
    const contactEmail = contact.email || contact.emails?.[0];

    (async () => {
      // Step 1: Fetch email threads (if contact has email)
      let latestThreadId: string | undefined;
      if (contactEmail) {
        setLoadingThreads(true);
        setLoadingLatestInbound(true);
        setLatestMessage(null);
        try {
          const res = await fetch(`${emailApiBase()}/search`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ query: `from:${contactEmail} OR to:${contactEmail}`, max: 10 }),
          });
          if (res.ok && !cancelled) {
            const data = await res.json();
            const threads = (data.threads || []).map((t: any) => ({
              ...t,
              inbound: (t.from || '').toLowerCase().includes(contactEmail.toLowerCase()),
            }));
            setEmailThreads(threads);
            latestThreadId = threads[0]?.id;

            // Auto-load the latest thread to find the most recent inbound message
            if (threads.length > 0) {
              try {
                const threadRes = await fetch(`${emailApiBase()}/thread`, {
                  method: 'POST',
                  headers: getHeaders(),
                  body: JSON.stringify({ threadId: threads[0].id }),
                });
                if (threadRes.ok && !cancelled) {
                  const threadData = await threadRes.json();
                  const msgs: ThreadMessage[] = (threadData.messages || []).map((m: any) => ({
                    from: m.from || '',
                    date: m.date || '',
                    body: htmlToText((m.body || '').substring(0, 800)),
                    inbound: (m.from || '').toLowerCase().includes(contactEmail.toLowerCase()),
                  }));
                  setThreadMessages(prev => ({ ...prev, [threads[0].id]: msgs }));
                  const lastMsg = msgs[msgs.length - 1];
                  if (lastMsg && !cancelled) {
                    setLatestMessage({
                      subject: threads[0].subject,
                      body: lastMsg.body,
                      date: lastMsg.date,
                      from: lastMsg.from,
                      inbound: lastMsg.inbound,
                    });
                  }
                }
              } catch {
                // best effort
              }
            }
          }
          // Gmail 401 / not connected — continue to draft generation anyway
        } catch {
          // Gmail not connected or error — no threads shown
        } finally {
          if (!cancelled) {
            setLoadingThreads(false);
            setLoadingLatestInbound(false);
          }
        }
      }

      if (cancelled) return;

      // Step 2: Generate AI draft
      setIsGeneratingDraft(true);
      try {
        const res = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/crm-engine/draft-email`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({
            contactId: contact.id,
            actionType: 'follow up',
            ...(latestThreadId ? { threadId: latestThreadId } : {}),
          }),
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (data.context && !cancelled) setAiContext(data.context);
        const variant = data.variants?.find((v: any) => v.tone === 'direct') || data.variants?.[0];
        if (variant && !cancelled && !userEditedRef.current) {
          setSubject(variant.subject);
          const firstName = contact.firstName || (contact.name || '').split(' ')[0] || '';
          setBody(firstName ? `Hey ${firstName},\n\n${variant.body}` : variant.body);
        }
      } catch {
        // Fall back to template — already set
      } finally {
        if (!cancelled) setIsGeneratingDraft(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, contact.id]);

  // Fetch full thread messages when expanded
  const handleExpandThread = async (threadId: string) => {
    if (expandedThreadId === threadId) {
      setExpandedThreadId(null);
      return;
    }
    setExpandedThreadId(threadId);
    if (threadMessages[threadId]) return; // already loaded
    setLoadingThreadDetail(threadId);
    const contactEmail = (contact.email || contact.emails?.[0] || '').toLowerCase();
    try {
      const res = await fetch(`${emailApiBase()}/thread`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ threadId }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const messages: ThreadMessage[] = (data.messages || []).map((m: any) => ({
        from: m.from || '',
        date: m.date || '',
        body: htmlToText((m.body || '').substring(0, 800)),
        inbound: (m.from || '').toLowerCase().includes(contactEmail),
      }));
      setThreadMessages(prev => ({ ...prev, [threadId]: messages }));
    } catch {
      // best effort
    } finally {
      setLoadingThreadDetail(null);
    }
  };

  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [savedDraft, setSavedDraft] = useState(false);

  // localStorage key for the dismiss-time draft stash. null means we will
  // NOT stash for this contact (no id and no email). See composeDraftStash.
  const draftStashKey = buildStashKey(contact);

  // Track the post-send/post-save 1.2s deferred-close timer so a parent-side
  // contact swap during that window doesn't fire onClose on the new modal
  // state. Cleared by the reset effect on isOpen/contact change.
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, [isOpen, contact]);

  // Dismiss the compose modal. If the user typed anything beyond the
  // auto-generated initial subject/body, fire a fire-and-forget draft save so
  // the work isn't lost (#2400). All visible close affordances — X, backdrop,
  // Escape, and the Cancel button — route through here for consistency.
  //
  // The fetch is fire-and-forget (the modal unmounts before the network
  // round-trip completes), so a 4xx silently dropped the user's work. To
  // recover that case we stash the draft to localStorage BEFORE the POST and
  // clear the stash only on a 2xx. On the next mount, if a stash exists for
  // this contact, it's loaded back into the editor.
  const handleDismiss = () => {
    const hasUserContent = userEditedRef.current && to.trim() && body.trim();
    if (hasUserContent && !sent && !savedDraft) {
      const attachmentsPayload = attachments.length > 0
        ? { attachments: attachments.map(a => ({ filename: a.filename, mimeType: a.mimeType, data: a.data })) }
        : {};
      // Stash BEFORE the POST so a 4xx / network failure can't silently
      // swallow user content. Base64 attachment data is intentionally omitted
      // from the stash (5MB localStorage quota) — user re-attaches on next
      // mount. writeDraftStash skips the write when draftStashKey is null
      // (anonymous contact with no stable identifier).
      writeDraftStash(draftStashKey, {
        to: to.trim(),
        subject,
        body,
        attachmentNames: attachments.map(a => a.filename),
      });
      fetch(`${emailApiBase()}/drafts`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ to: to.trim(), subject, body, ...attachmentsPayload }),
      })
        .then((res) => {
          if (res.ok) {
            // Upstream accepted the draft — clear the local stash.
            clearDraftStash(draftStashKey);
          } else {
            // Leave the stash in place for next mount to recover.
            console.warn('[ComposeEmailModal] auto-save draft non-2xx:', res.status);
          }
        })
        .catch((err) => {
          // Network failure — leave stash so next mount can recover.
          console.error('[ComposeEmailModal] auto-save draft failed:', err);
        });
    }
    onClose();
  };

  // Escape key triggers the same save-on-dismiss path as X / backdrop (#2400).
  useEscapeKey(handleDismiss, isOpen);

  // Image enlarged via the shared lightbox.
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  // Add files from any source (picker, drag-drop, paste). Image files get an
  // object-URL preview for the thumbnail + click-to-enlarge.
  const addComposeFiles = async (fileList: FileList | File[]) => {
    for (const file of Array.from(fileList)) {
      if (file.size > 25 * 1024 * 1024) {
        setError(`File "${file.name}" exceeds 25MB limit`);
        continue;
      }
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]); // strip data:...;base64, prefix
        };
        reader.readAsDataURL(file);
      });
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
      setAttachments(prev => [...prev, {
        id: nanoid(),
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        data: base64,
        size: file.size,
        previewUrl,
      }]);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) await addComposeFiles(e.target.files);
    e.target.value = ''; // reset input
  };

  const { isDragOver, dragProps: composeDragProps, onPaste: handleComposePaste } = useImageDropZone(addComposeFiles);

  const removeAttachment = (id: string) => {
    setAttachments(prev => {
      const att = prev.find(a => a.id === id);
      if (att?.previewUrl) URL.revokeObjectURL(att.previewUrl);
      return prev.filter(a => a.id !== id);
    });
  };

  const handleSend = async () => {
    if (!to.trim()) { setError('Recipient email is required'); return; }
    setIsSending(true);
    setError(null);
    try {
      const res = await fetch(`${emailApiBase()}/send`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          to: to.trim(), subject, body,
          ...(attachments.length > 0 ? { attachments: attachments.map(a => ({ filename: a.filename, mimeType: a.mimeType, data: a.data })) } : {}),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Send failed' }));
        const msg = data.error || `Send failed: ${res.status}`;
        if (res.status === 401) {
          throw new Error('Google account needs to be reconnected. Go to Integrations and re-connect Google.');
        }
        throw new Error(msg);
      }
      setSent(true);
      // Clear any stashed draft now that the message is upstream.
      clearDraftStash(draftStashKey);
      onSend?.({ to: to.trim(), subject, body, attachmentNames: attachments.map(a => a.filename) });
      closeTimeoutRef.current = setTimeout(onClose, 1200);
    } catch (err: any) {
      setError(err.message || 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveAsDraft = async () => {
    if (!to.trim()) { setError('Recipient email is required'); return; }
    if (!body.trim()) { setError('Message body is required'); return; }
    setIsSavingDraft(true);
    setError(null);
    try {
      const res = await fetch(`${emailApiBase()}/drafts`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          to: to.trim(), subject, body,
          ...(attachments.length > 0 ? { attachments: attachments.map(a => ({ filename: a.filename, mimeType: a.mimeType, data: a.data })) } : {}),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Save failed' }));
        const msg = data.error || `Save failed: ${res.status}`;
        if (res.status === 401) {
          throw new Error('Google account needs to be reconnected. Go to Integrations and re-connect Google.');
        }
        throw new Error(msg);
      }
      setSavedDraft(true);
      // Clear any stashed draft now that the explicit save succeeded.
      clearDraftStash(draftStashKey);
      closeTimeoutRef.current = setTimeout(onClose, 1200);
    } catch (err: any) {
      setError(err.message || 'Failed to save draft');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleInsertResponse = (r: EmailResponse) => {
    const rendered = renderTemplate(r.content, contact);
    const textarea = bodyRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = body.slice(0, start);
      const after = body.slice(end);
      const needsNewline = before.length > 0 && !before.endsWith('\n') ? '\n' : '';
      setBody(before + needsNewline + rendered + after);
      // Move cursor after inserted text
      setTimeout(() => {
        const newPos = start + needsNewline.length + rendered.length;
        textarea.setSelectionRange(newPos, newPos);
        textarea.focus();
      }, 0);
    } else {
      setBody(prev => prev + (prev && !prev.endsWith('\n') ? '\n' : '') + rendered);
    }
  };

  const handleDeleteResponse = (id: string) => {
    if (onSaveResponses) {
      onSaveResponses(responses.filter(r => r.id !== id));
    }
  };

  const handleSaveResponse = () => {
    if (!onSaveResponses || !editName.trim() || !editContent.trim()) return;
    if (editingResponse) {
      // Update existing
      onSaveResponses(responses.map(r =>
        r.id === editingResponse.id
          ? { ...r, name: editName.trim(), content: editContent.trim(), stages: editStages.length > 0 ? editStages : undefined }
          : r
      ));
    } else {
      // Create new
      onSaveResponses([...responses, {
        id: nanoid(),
        name: editName.trim(),
        content: editContent.trim(),
        stages: editStages.length > 0 ? editStages : undefined,
      }]);
    }
    setView('manage');
    setEditingResponse(null);
  };

  const openEditResponse = (r?: EmailResponse) => {
    setEditingResponse(r || null);
    setEditName(r?.name ?? '');
    setEditContent(r?.content ?? '');
    setEditStages(r?.stages ?? []);
    setView('edit-response');
  };

  if (!isOpen) return null;
  const portalRoot = document.getElementById('modal-portal-root');
  if (!portalRoot) return null;

  const relevantResponses = getRelevantResponses(responses, contact.stage);
  const recentNotes = (contact.notes || [])
    .slice()
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))
    .slice(0, 3);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid var(--color-border)',
    background: 'var(--color-surface)',
    color: 'var(--color-text)',
    fontSize: '14px',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--color-text-faint)',
    display: 'block',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const contextBadgeStyle: React.CSSProperties = {
    padding: '2px 8px',
    borderRadius: '10px',
    border: '1px solid var(--color-border, #ddd)',
    background: 'var(--color-surface, white)',
    color: 'var(--color-text-muted, #666)',
    fontWeight: 500,
    textTransform: 'capitalize',
  };

  const modalContent = (
    <div
      // role="dialog" + the *-overlay class make this classless-inline overlay
      // detectable by useModulePaneEscapeClose's DOM guard (issue #3938), closing
      // the same data-loss double-close path where Esc could close both this
      // modal and the module pane on one keypress. Esc still runs handleDismiss
      // (useEscapeKey, which preventDefaults) so the draft-stash save fires.
      className="compose-email-modal-overlay"
      role="dialog"
      aria-modal="true"
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }}
      onClick={handleDismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        {...composeDragProps}
        className="xv-scroll"
        style={{
          position: 'relative',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '12px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
          width: '520px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          outline: isDragOver ? '2px dashed var(--color-accent, #1a73e8)' : undefined,
          outlineOffset: '-4px',
        }}
      >
        {isDragOver && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 5, display: 'flex', alignItems: 'center',
            justifyContent: 'center', pointerEvents: 'none', fontSize: 15, fontWeight: 600,
            color: 'var(--color-accent, #1a73e8)', borderRadius: 12,
            background: 'color-mix(in srgb, var(--color-surface, #fff) 78%, transparent)',
          }}>
            Drop image to attach
          </div>
        )}
        {/* Header */}
        <div style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-text)' }}>
            {view === 'compose' ? `Email ${contact.name || ''}` : view === 'ai-response' ? 'AI Drafts' : view === 'manage' ? 'Manage Responses' : (editingResponse ? 'Edit Response' : 'New Response')}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {(view === 'compose' || view === 'ai-response') && (
              <>
                <button
                  onClick={() => setView(view === 'ai-response' ? 'compose' : 'ai-response')}
                  style={{
                    background: view === 'ai-response' ? 'var(--xavior-blue-bg, #e8f0fe)' : 'none',
                    border: '1px solid var(--color-border)', borderRadius: '6px',
                    cursor: 'pointer', padding: '4px 10px',
                    color: view === 'ai-response' ? 'var(--color-brand)' : 'var(--color-text-muted)', fontSize: '12px',
                  }}
                >
                  {view === 'ai-response' ? 'Compose' : 'AI Drafts'}
                </button>
                <button
                  onClick={() => setShowSnippetLibrary(true)}
                  style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer', padding: '4px 10px', color: 'var(--color-text-muted)', fontSize: '12px' }}
                >
                  Snippets
                </button>
              </>
            )}
            {view === 'compose' && onSaveResponses && (
              <button
                onClick={() => setView('manage')}
                style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer', padding: '4px 10px', color: 'var(--color-text-muted)', fontSize: '12px' }}
              >
                Responses
              </button>
            )}
            <CloseButton onClick={handleDismiss} label="Close compose email" />
          </div>
        </div>

        {/* ── COMPOSE VIEW ── */}
        {view === 'compose' && (
          <>
            {/* Latest inbound message — what you're replying to */}
            {loadingLatestInbound && !latestMessage && (
              <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--color-border)', fontSize: '12px', color: 'var(--color-text-faint)' }}>
                Loading latest message...
              </div>
            )}
            {latestMessage && (
              <div style={{
                padding: '10px 16px',
                borderBottom: '1px solid var(--color-border)',
                background: 'var(--color-surface-hover, #f8f9fa)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <span style={{
                    fontSize: '9px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: latestMessage.inbound ? '#0369a1' : '#166534',
                    background: latestMessage.inbound ? 'var(--xavior-blue-bg, #e0f2fe)' : '#dcfce7',
                    padding: '1px 6px',
                    borderRadius: '3px',
                  }}>{latestMessage.inbound ? 'Inbound' : 'Outbound'}</span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text)' }}>
                    {latestMessage.from.split('<')[0].trim()}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--color-text-faint)', marginLeft: 'auto' }}>
                    {new Date(latestMessage.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </span>
                </div>
                <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                  Re: {latestMessage.subject}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: 'var(--color-text-muted)',
                  lineHeight: 1.5,
                  maxHeight: '80px',
                  overflow: 'hidden',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {latestMessage.body.length > 300 ? latestMessage.body.slice(0, 300) + '...' : latestMessage.body}
                </div>
              </div>
            )}

            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
              {/* To + Subject row */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>To</label>
                  <input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com" style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Subject</label>
                  <input type="text" value={subject} onChange={(e) => { userEditedRef.current = true; setSubject(e.target.value); }} placeholder="Email subject" style={inputStyle} />
                </div>
              </div>

              {/* Responses chips */}
              {relevantResponses.length > 0 && (
                <div>
                  <label style={labelStyle}>Insert Response</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {relevantResponses.map(r => (
                      <button
                        key={r.id}
                        onClick={() => handleInsertResponse(r)}
                        title={r.content}
                        style={{
                          padding: '5px 12px',
                          borderRadius: '16px',
                          border: '1px solid var(--color-border)',
                          background: 'var(--color-surface)',
                          color: 'var(--color-text)',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-surface-hover)'; e.currentTarget.style.borderColor = 'var(--color-border-strong)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-surface)'; e.currentTarget.style.borderColor = 'var(--color-border)'; }}
                      >
                        {r.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Body */}
              <div style={{ position: 'relative' }}>
                <label style={labelStyle}>Message</label>
                <textarea
                  ref={bodyRef}
                  value={body}
                  onChange={(e) => { userEditedRef.current = true; setBody(e.target.value); }}
                  onPaste={handleComposePaste}
                  placeholder="Write your message..."
                  rows={8}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', opacity: isGeneratingDraft ? 0.5 : 1, transition: 'opacity 0.2s' }}
                />
                {isGeneratingDraft && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '12px',
                    color: 'var(--color-text-muted, #888)',
                    fontWeight: 500,
                    background: 'var(--color-surface, white)',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    border: '1px solid var(--color-border, #ddd)',
                  }}>
                    Personalizing with AI...
                  </div>
                )}
              </div>

              {/* What AI knows about this contact */}
              {(aiContext || contact.stage || recentNotes.length > 0) && (
                <div style={{
                  background: 'var(--color-surface-hover, #f5f5f5)',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  border: '1px solid var(--color-border, #e5e5e5)',
                }}>
                  <label style={{ ...labelStyle, marginBottom: '6px' }}>
                    {isGeneratingDraft ? 'Loading context...' : 'What AI knows'}
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', fontSize: '11px' }}>
                    {(aiContext?.stage || contact.stage) && (
                      <span style={contextBadgeStyle}>Stage: {aiContext?.stage || contact.stage}</span>
                    )}
                    {aiContext?.sentiment && (
                      <span style={contextBadgeStyle}>Sentiment: {aiContext.sentiment}</span>
                    )}
                    {aiContext?.interestLevel && (
                      <span style={contextBadgeStyle}>Interest: {aiContext.interestLevel}</span>
                    )}
                    {aiContext?.relationshipState && (
                      <span style={contextBadgeStyle}>{aiContext.relationshipState.replace(/_/g, ' ')}</span>
                    )}
                    {aiContext?.followUpScore != null && (
                      <span style={{
                        ...contextBadgeStyle,
                        color: aiContext.followUpScore >= 70 ? '#16a34a' : aiContext.followUpScore >= 40 ? '#ca8a04' : '#dc2626',
                        borderColor: aiContext.followUpScore >= 70 ? '#bbf7d0' : aiContext.followUpScore >= 40 ? '#fef08a' : '#fecaca',
                        background: aiContext.followUpScore >= 70 ? '#f0fdf4' : aiContext.followUpScore >= 40 ? '#fefce8' : '#fef2f2',
                      }}>
                        Score: {aiContext.followUpScore}/100
                      </span>
                    )}
                    {aiContext?.nextStep && (
                      <span style={contextBadgeStyle}>Next: {aiContext.nextStep}</span>
                    )}
                  </div>
                  {aiContext?.summary && (
                    <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '6px', lineHeight: 1.4 }}>
                      {aiContext.summary}
                    </div>
                  )}
                  {(aiContext?.recentNotes?.length ?? 0) > 0 && (
                    <div style={{ marginTop: '6px' }}>
                      {aiContext!.recentNotes.map((note, i) => (
                        <div key={i} style={{ fontSize: '11px', color: 'var(--color-text-muted)', lineHeight: 1.4 }}>
                          - {note}
                        </div>
                      ))}
                    </div>
                  )}
                  {!aiContext && recentNotes.length > 0 && (
                    <div style={{ marginTop: '4px' }}>
                      {recentNotes.map(n => (
                        <div key={n.id} style={{ fontSize: '11px', color: 'var(--color-text-muted)', lineHeight: 1.4 }}>
                          - {n.content.length > 80 ? n.content.slice(0, 80) + '...' : n.content}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Communication history */}
              {(emailThreads.length > 0 || loadingThreads) && (
                <div style={{
                  background: 'var(--color-surface-hover, #f5f5f5)',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  border: '1px solid var(--color-border, #e5e5e5)',
                }}>
                  <label style={{ ...labelStyle, marginBottom: '6px' }}>
                    {loadingThreads ? 'Loading emails...' : `Email history (${emailThreads.length})`}
                  </label>
                  <div className="xv-scroll" style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '200px', overflowY: 'auto' }}>
                    {emailThreads.map(thread => (
                      <div key={thread.id}>
                        <div
                          onClick={() => handleExpandThread(thread.id)}
                          style={{
                            fontSize: '12px',
                            padding: '5px 8px',
                            background: expandedThreadId === thread.id ? 'var(--color-surface, white)' : 'transparent',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            display: 'flex',
                            gap: '6px',
                            alignItems: 'center',
                            transition: 'background 0.1s',
                          }}
                        >
                          <span style={{
                            fontSize: '8px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            flexShrink: 0,
                            ...(thread.inbound
                              ? { color: '#0369a1', background: 'var(--xavior-blue-bg, #e0f2fe)' }
                              : { color: 'var(--color-text-faint)', background: '#f3f4f6' }),
                          }}>
                            {thread.inbound ? 'IN' : 'OUT'}
                          </span>
                          <span style={{ color: 'var(--color-text-faint)', fontSize: '11px', flexShrink: 0 }}>
                            {new Date(thread.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          <span style={{ fontWeight: 500, color: 'var(--color-text)', flexShrink: 0, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {thread.subject}
                          </span>
                          <span style={{ color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, fontSize: '11px' }}>
                            {thread.snippet}
                          </span>
                        </div>
                        {expandedThreadId === thread.id && (
                          <div style={{ padding: '4px 8px 8px 30px' }}>
                            {loadingThreadDetail === thread.id ? (
                              <div style={{ fontSize: '11px', color: 'var(--color-text-faint)' }}>Loading messages...</div>
                            ) : (threadMessages[thread.id] || []).map((msg, i) => (
                              <div key={i} style={{
                                fontSize: '11px',
                                color: 'var(--color-text-muted)',
                                padding: '5px 0',
                                borderTop: i > 0 ? '1px solid var(--color-border, #eee)' : 'none',
                                lineHeight: 1.4,
                                borderLeft: msg.inbound ? '2px solid #0369a1' : '2px solid #d1d5db',
                                paddingLeft: '8px',
                                marginLeft: '4px',
                              }}>
                                <div style={{ display: 'flex', gap: '6px', marginBottom: '2px', alignItems: 'center' }}>
                                  <span style={{
                                    fontSize: '7px', fontWeight: 700, textTransform: 'uppercase',
                                    padding: '0px 3px', borderRadius: '2px',
                                    ...(msg.inbound ? { color: '#0369a1', background: 'var(--xavior-blue-bg, #e0f2fe)' } : { color: 'var(--color-text-faint)', background: '#f3f4f6' }),
                                  }}>
                                    {msg.inbound ? 'IN' : 'OUT'}
                                  </span>
                                  <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>
                                    {msg.from.split('<')[0].trim() || msg.from}
                                  </span>
                                  <span style={{ color: 'var(--color-text-faint)' }}>
                                    {new Date(msg.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                  </span>
                                </div>
                                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                  {msg.body.length > 300 ? msg.body.slice(0, 300) + '...' : msg.body}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Attachments */}
              {attachments.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {attachments.map(att => (
                    <div key={att.id} style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '4px 10px', borderRadius: '6px',
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-surface-hover, #f8f9fa)',
                      fontSize: '12px',
                    }}>
                      {att.previewUrl ? (
                        <img
                          src={att.previewUrl}
                          alt={att.filename}
                          title={`Click to view ${att.filename}`}
                          onClick={() => att.previewUrl && setLightbox({ src: att.previewUrl, alt: att.filename })}
                          style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, cursor: 'zoom-in' }}
                        />
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}>
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                        </svg>
                      )}
                      <span style={{ color: 'var(--color-text)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.filename}</span>
                      <span style={{ color: 'var(--color-text-faint)', fontSize: '11px' }}>{att.size < 1024 * 1024 ? `${Math.round(att.size / 1024)}KB` : `${(att.size / (1024 * 1024)).toFixed(1)}MB`}</span>
                      <button onClick={() => removeAttachment(att.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: 'var(--color-text-faint)', fontSize: '14px', lineHeight: 1 }} title="Remove">×</button>
                    </div>
                  ))}
                </div>
              )}

              {error && <div style={{ color: '#dc2626', fontSize: '13px' }}>{error}</div>}
              {sent && <div style={{ color: '#16a34a', fontSize: '13px', fontWeight: 500 }}>Email sent!</div>}
              {savedDraft && <div style={{ color: '#16a34a', fontSize: '13px', fontWeight: 500 }}>Draft saved to Gmail!</div>}
            </div>

            {/* Footer */}
            <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} style={{ display: 'none' }} />
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'flex-end', gap: '8px', flexShrink: 0 }}>
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer', padding: '8px 10px', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', marginRight: 'auto' }}
                title="Attach files"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
                Attach
              </button>
              <button onClick={handleDismiss} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text-subtle)', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
              <button
                onClick={handleSaveAsDraft}
                disabled={isSavingDraft || savedDraft || sent || !to.trim()}
                style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #16a34a', background: isSavingDraft || savedDraft ? '#bbf7d0' : 'var(--color-surface)', color: isSavingDraft || savedDraft ? '#166534' : '#16a34a', cursor: isSavingDraft || savedDraft || sent ? 'default' : 'pointer', fontSize: '14px', fontWeight: 500 }}
              >
                {isSavingDraft ? 'Saving...' : savedDraft ? 'Saved!' : 'Save Draft'}
              </button>
              <button
                onClick={handleSend}
                disabled={isSending || sent || !to.trim()}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: isSending || sent ? '#93c5fd' : '#2563eb', color: 'white', cursor: isSending || sent ? 'default' : 'pointer', fontSize: '14px', fontWeight: 500 }}
              >
                {isSending ? 'Sending...' : sent ? 'Sent!' : 'Send'}
              </button>
            </div>
          </>
        )}

        {/* ── AI RESPONSE VIEW ── */}
        {view === 'ai-response' && contact.id && (
          <div className="xv-scroll" style={{ padding: '0 20px', flex: 1, overflowY: 'auto' }}>
            <AIResponsePanel
              contactId={contact.id}
              contactName={contact.name || contact.firstName || 'Contact'}
              onUseDraft={(draftSubject, draftBody) => {
                setSubject(draftSubject);
                setBody(draftBody);
                setView('compose');
              }}
            />
          </div>
        )}

        {/* ── MANAGE RESPONSES VIEW ── */}
        {view === 'manage' && (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {responses.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--color-text-muted)', padding: '20px 0', textAlign: 'center' }}>
                No responses yet. Add reusable snippets for quick email composition.
              </div>
            )}
            {responses.map(r => (
              <div
                key={r.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-surface)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--color-text)' }}>{r.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.content}
                  </div>
                  {r.stages && r.stages.length > 0 && (
                    <div style={{ fontSize: '11px', color: 'var(--color-text-faint)', marginTop: '4px' }}>
                      Stages: {r.stages.join(', ')}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                  <button
                    onClick={() => openEditResponse(r)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-text-muted)', fontSize: '13px' }}
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteResponse(r.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-text-muted)', fontSize: '13px' }}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px' }}>
              <button
                onClick={() => setView('compose')}
                style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text-subtle)', cursor: 'pointer', fontSize: '14px' }}
              >
                Back
              </button>
              <button
                onClick={() => openEditResponse()}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#2563eb', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
              >
                + Add Response
              </button>
            </div>
          </div>
        )}

        {/* ── EDIT RESPONSE VIEW ── */}
        {view === 'edit-response' && (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder='e.g. "Fund closing date"'
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Content</label>
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                placeholder="The snippet text. Use {{firstName}}, {{name}}, {{company}} for variables."
                rows={4}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>
            {stages.length > 0 && (
              <div>
                <label style={labelStyle}>Show for stages (leave empty for all)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {stages.map(s => {
                    const active = editStages.includes(s);
                    return (
                      <button
                        key={s}
                        onClick={() => setEditStages(active ? editStages.filter(x => x !== s) : [...editStages, s])}
                        style={{
                          padding: '4px 12px',
                          borderRadius: '14px',
                          border: active ? '2px solid #2563eb' : '1px solid var(--color-border)',
                          background: active ? '#eff6ff' : 'var(--color-surface)',
                          color: active ? '#1d4ed8' : 'var(--color-text-muted)',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: active ? 600 : 400,
                        }}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '4px' }}>
              <button
                onClick={() => { setView('manage'); setEditingResponse(null); }}
                style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text-subtle)', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveResponse}
                disabled={!editName.trim() || !editContent.trim()}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: !editName.trim() || !editContent.trim() ? '#93c5fd' : '#2563eb', color: 'white', cursor: !editName.trim() || !editContent.trim() ? 'default' : 'pointer', fontSize: '14px', fontWeight: 500 }}
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, portalRoot)}
      {lightbox && (
        <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}
      <SnippetLibrary
        isOpen={showSnippetLibrary}
        onClose={() => setShowSnippetLibrary(false)}
        stages={stages}
      />
    </>
  );
}
